import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aiOpsApi } from '@/lib/api';

interface Stats {
  sessions: { total: number; active: number };
  messages: { total: number; avgTokensPerMessage: number };
  tokens: { total: number; last24h: number; last7d: number };
  cost: { total: number; last24h: number; last7d: number };
}

interface UsageDay {
  date: string;
  tokens: number;
  messages: number;
  cost: number;
}

interface TopUser {
  miembroId: string;
  nombre: string;
  email: string | null;
  hogar: string;
  sessions: number;
  tokens: number;
  estimatedCost: number;
}

interface RecentSession {
  id: string;
  title: string;
  status: string;
  miembro: { nombre: string; email: string };
  hogar: string;
  messageCount: number;
  totalTokens: number;
  estimatedCost: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SessionDetails {
  id: string;
  title: string;
  status: string;
  miembro: { nombre: string; email: string };
  hogar: string;
  totalTokens: number;
  estimatedCost: number;
  messages: Array<{
    id: string;
    role: string;
    content: string;
    tokens: number;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
  simulations: Array<{
    id: string;
    type: string;
    title: string;
    inputData: Record<string, unknown>;
    resultData: Record<string, unknown>;
    applied: boolean;
    createdAt: string;
  }>;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function AIOps() {
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [usageDays, setUsageDays] = useState(30);

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['ai-ops-stats'],
    queryFn: async () => {
      const response = await aiOpsApi.getStats();
      return response.data as Stats;
    },
  });

  // Fetch usage by day
  const { data: usageData } = useQuery({
    queryKey: ['ai-ops-usage', usageDays],
    queryFn: async () => {
      const response = await aiOpsApi.getUsageByDay(usageDays);
      return response.data as UsageDay[];
    },
  });

  // Fetch top users
  const { data: topUsersData } = useQuery({
    queryKey: ['ai-ops-top-users'],
    queryFn: async () => {
      const response = await aiOpsApi.getTopUsers(10);
      return response.data as TopUser[];
    },
  });

  // Fetch recent sessions
  const { data: recentSessionsData } = useQuery({
    queryKey: ['ai-ops-recent-sessions'],
    queryFn: async () => {
      const response = await aiOpsApi.getRecentSessions(20);
      return response.data as RecentSession[];
    },
  });

  // Fetch session details when selected
  const { data: sessionDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['ai-ops-session', selectedSession],
    queryFn: async () => {
      if (!selectedSession) return null;
      const response = await aiOpsApi.getSessionDetails(selectedSession);
      return response.data as SessionDetails;
    },
    enabled: !!selectedSession,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('es-MX').format(value);
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-MX', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  // Calculate max tokens for chart scaling
  const maxTokens = usageData?.reduce((max, day) => Math.max(max, day.tokens), 0) || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Operations</h1>
          <p className="text-gray-600">Metricas y monitoreo del Chat AI (Claude)</p>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : statsData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sesiones Totales</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(statsData.sessions.total)}</p>
                <p className="text-sm text-green-600">{statsData.sessions.active} activas</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <ChatIcon className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Mensajes Totales</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(statsData.messages.total)}</p>
                <p className="text-sm text-gray-500">~{statsData.messages.avgTokensPerMessage} tokens/msg</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <MessageIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tokens Totales</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(statsData.tokens.total)}</p>
                <p className="text-sm text-gray-500">
                  24h: {formatNumber(statsData.tokens.last24h)} | 7d: {formatNumber(statsData.tokens.last7d)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TokenIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Costo Estimado</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(statsData.cost.total)}</p>
                <p className="text-sm text-gray-500">
                  24h: {formatCurrency(statsData.cost.last24h)} | 7d: {formatCurrency(statsData.cost.last7d)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CostIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Usage Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Uso de Tokens por Dia</h2>
          <select
            value={usageDays}
            onChange={(e) => setUsageDays(parseInt(e.target.value))}
            className="px-3 py-1 border rounded-lg text-sm"
          >
            <option value={7}>Ultimos 7 dias</option>
            <option value={14}>Ultimos 14 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={60}>Ultimos 60 dias</option>
          </select>
        </div>
        {usageData && usageData.length > 0 ? (
          <div className="h-48 flex items-end space-x-1">
            {usageData.map((day) => (
              <div
                key={day.date}
                className="flex-1 flex flex-col items-center group relative"
              >
                <div
                  className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors"
                  style={{ height: `${(day.tokens / maxTokens) * 100}%`, minHeight: '4px' }}
                />
                <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                  <div>{day.date}</div>
                  <div>{formatNumber(day.tokens)} tokens</div>
                  <div>{day.messages} mensajes</div>
                  <div>{formatCurrency(day.cost)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500">
            No hay datos de uso disponibles
          </div>
        )}
        {usageData && usageData.length > 0 && (
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{usageData[0]?.date}</span>
            <span>{usageData[usageData.length - 1]?.date}</span>
          </div>
        )}
      </div>

      {/* Two columns: Top Users and Recent Sessions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Users */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Top Usuarios por Tokens</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sesiones</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tokens</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topUsersData?.map((user, index) => (
                  <tr key={user.miembroId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded-full text-xs font-medium mr-2">
                          {index + 1}
                        </span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.nombre}</div>
                          <div className="text-xs text-gray-500">{user.hogar}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{user.sessions}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{formatNumber(user.tokens)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(user.estimatedCost)}</td>
                  </tr>
                ))}
                {(!topUsersData || topUsersData.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No hay datos de usuarios
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Sesiones Recientes</h2>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titulo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Msgs</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tokens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentSessionsData?.map((session) => (
                  <tr
                    key={session.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedSession(session.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">{session.title}</div>
                      <div className="text-xs text-gray-500">{formatDate(session.lastMessageAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{session.miembro.nombre}</div>
                      <div className="text-xs text-gray-500">{session.hogar}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{session.messageCount}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm text-gray-900">{formatNumber(session.totalTokens)}</div>
                      <div className="text-xs text-gray-500">{formatCurrency(session.estimatedCost)}</div>
                    </td>
                  </tr>
                ))}
                {(!recentSessionsData || recentSessionsData.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No hay sesiones recientes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {detailsLoading ? 'Cargando...' : sessionDetails?.title || 'Detalles de Sesion'}
                </h3>
                {sessionDetails && (
                  <p className="text-sm text-gray-500">
                    {sessionDetails.miembro.nombre} - {sessionDetails.hogar} |{' '}
                    {formatNumber(sessionDetails.totalTokens)} tokens ({formatCurrency(sessionDetails.estimatedCost)})
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="p-2 hover:bg-gray-200 rounded-full"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {detailsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : sessionDetails ? (
                <>
                  {/* Messages */}
                  <div className="space-y-4">
                    {sessionDetails.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            message.role === 'user'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                          <div
                            className={`text-xs mt-1 ${
                              message.role === 'user' ? 'text-indigo-200' : 'text-gray-500'
                            }`}
                          >
                            {formatDate(message.createdAt)}
                            {message.tokens > 0 && ` | ${message.tokens} tokens`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Simulations */}
                  {sessionDetails.simulations.length > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Simulaciones</h4>
                      <div className="space-y-2">
                        {sessionDetails.simulations.map((sim) => (
                          <div key={sim.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-medium">{sim.title || sim.type}</span>
                                <span
                                  className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                    sim.applied
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}
                                >
                                  {sim.applied ? 'Aplicada' : 'Pendiente'}
                                </span>
                              </div>
                              <span className="text-gray-500 text-xs">{formatDate(sim.createdAt)}</span>
                            </div>
                            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                              {JSON.stringify(sim.resultData, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No se pudo cargar la sesion
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Icons
function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  );
}

function TokenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  );
}

function CostIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
